import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, XCircle, Mail, Clock, FileText, ArrowLeft } from 'lucide-react';
import { trackPurchase } from '../utils/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DCESuccessPage() {
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [orderDetails, setOrderDetails] = useState(null);
  const [error, setError] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId || !token) return;

    let attempts = 0;
    const maxAttempts = 5;
    const pollInterval = 2000;

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/api/dce/checkout/status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erreur lors de la vérification du paiement');
        }

        const data = await response.json();
        setOrderDetails(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          trackPurchase({
            transactionId: sessionId,
            planId: data.product || data.plan_id || 'dce_one_shot',
            value: typeof data.amount === 'number' ? data.amount : 0,
          });
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling if payment still processing
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        console.error('Error checking payment:', err);
        setError(err.message);
        setStatus('error');
      }
    };

    checkPaymentStatus();
  }, [sessionId, token]);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Session invalide</h2>
            <p className="text-slate-600 mb-4">Aucune session de paiement trouvée.</p>
            <Link to="/">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-violet-50 flex items-center justify-center p-4">
      <Card className="max-w-lg w-full shadow-xl">
        <CardHeader className="text-center pb-2">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 text-violet-600 mx-auto mb-4 animate-spin" />
              <CardTitle className="text-2xl text-slate-900">Vérification du paiement...</CardTitle>
            </>
          )}
          {status === 'success' && (
            <>
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-2xl text-slate-900">Paiement confirmé !</CardTitle>
            </>
          )}
          {status === 'expired' && (
            <>
              <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
              <CardTitle className="text-2xl text-slate-900">Session expirée</CardTitle>
            </>
          )}
          {(status === 'error' || status === 'timeout') && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <CardTitle className="text-2xl text-slate-900">Erreur</CardTitle>
            </>
          )}
        </CardHeader>
        
        <CardContent className="space-y-6">
          {status === 'checking' && (
            <p className="text-center text-slate-600">
              Veuillez patienter pendant que nous vérifions votre paiement...
            </p>
          )}

          {status === 'success' && orderDetails && (
            <>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 text-center font-medium">
                  {orderDetails.message}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Mail className="w-5 h-5 text-violet-600" />
                  <div>
                    <p className="text-sm text-slate-500">Email de livraison</p>
                    <p className="font-medium text-slate-900">Votre adresse email enregistrée</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <Clock className="w-5 h-5 text-violet-600" />
                  <div>
                    <p className="text-sm text-slate-500">Délai de livraison</p>
                    <p className="font-medium text-slate-900">Sous 24 heures ouvrées</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <FileText className="w-5 h-5 text-violet-600" />
                  <div>
                    <p className="text-sm text-slate-500">Montant payé</p>
                    <p className="font-medium text-slate-900">{orderDetails.amount?.toFixed(2)} €</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Link to="/dashboard" className="flex-1">
                  <Button className="w-full bg-violet-600 hover:bg-violet-700">
                    Voir mes commandes
                  </Button>
                </Link>
                <Link to="/" className="flex-1">
                  <Button variant="outline" className="w-full">
                    Retour à l'accueil
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'expired' && (
            <>
              <p className="text-center text-slate-600">
                La session de paiement a expiré. Veuillez réessayer.
              </p>
              <Link to="/" className="block">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </Link>
            </>
          )}

          {(status === 'error' || status === 'timeout') && (
            <>
              <p className="text-center text-slate-600">
                {error || "Une erreur est survenue lors de la vérification du paiement."}
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm text-center">
                  Si vous avez été débité, votre commande sera traitée. 
                  Contactez-nous si vous ne recevez pas votre DCE sous 24h.
                </p>
              </div>
              <Link to="/" className="block">
                <Button className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à l'accueil
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
